import { useEffect, useState } from "react";
import { Drawer, Modal, Tooltip, message } from "antd";
import { careService } from "../../services/careService";
import { useFileUpload } from "../../hooks/useFileUpload";
import { COMPLIANCE_EVIDENCE_TYPES } from "../../pages/care/complianceEvidenceRegistry";

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const formatFileSize = (size) => {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const attachmentName = (attachment = {}, index = 0) =>
  firstText(
    attachment.description,
    attachment.fileName,
    attachment.name,
    `Attachment ${index + 1}`,
  );

const attachmentUrl = (attachment = {}) =>
  firstText(
    attachment.localPreviewUrl,
    attachment.previewUrl,
    attachment.signedUrl,
    attachment.downloadUrl,
    attachment.url,
  );

const isPhoto = (attachment = {}) => {
  const contentType = String(
    attachment.contentType || attachment.type || "",
  ).toLowerCase();
  const fileName = String(
    attachment.fileName || attachment.name || "",
  ).toLowerCase();
  return (
    contentType.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|heic)$/.test(fileName)
  );
};

const defaultValueFor = (field) => {
  if (field.type === "datetime") {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 16);
  }
  if (field.type === "photo") return [];
  return "";
};

const defaultFormFor = (config) =>
  Object.fromEntries(
    config.fields.map((field) => [field.name, defaultValueFor(field)]),
  );

export default function GenericEvidenceForm({
  open,
  evidenceType,
  workspaceId,
  actorId,
  record,
  readOnly,
  onClose,
  onSaved,
}) {
  const config = COMPLIANCE_EVIDENCE_TYPES[evidenceType];
  const { uploadFile, uploading } = useFileUpload();
  const [form, setForm] = useState(() => formFromRecord(config, record));
  const [saving, setSaving] = useState(false);
  const [previewingAttachment, setPreviewingAttachment] = useState(null);

  useEffect(() => {
    if (config) setForm(formFromRecord(config, record));
  }, [config, record, open]);

  if (!config) return null;

  const resetForm = () => setForm(defaultFormFor(config));

  const updateField = (name, value) =>
    setForm((current) => ({ ...current, [name]: value }));

  const addPhotos = (fieldName, event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const attachments = files.map((file) => ({
      id: `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`,
      file,
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      size: file.size,
      description: "",
      localPreviewUrl: URL.createObjectURL(file),
    }));
    updateField(fieldName, [...(form[fieldName] || []), ...attachments]);
    event.target.value = "";
  };

  const updateAttachmentDescription = (
    fieldName,
    attachmentId,
    description,
  ) => {
    updateField(
      fieldName,
      (form[fieldName] || []).map((attachment) =>
        attachment.id === attachmentId
          ? { ...attachment, description }
          : attachment,
      ),
    );
  };

  const removeAttachment = (fieldName, attachmentId) => {
    const removed = (form[fieldName] || []).find(
      (attachment) => attachment.id === attachmentId,
    );
    if (removed?.localPreviewUrl) URL.revokeObjectURL(removed.localPreviewUrl);
    updateField(
      fieldName,
      (form[fieldName] || []).filter(
        (attachment) => attachment.id !== attachmentId,
      ),
    );
  };

  const previewAttachment = (attachment) => {
    if (!attachmentUrl(attachment)) {
      message.error("Preview is not available for this attachment.");
      return;
    }
    setPreviewingAttachment(attachment);
  };

  const uploadPhotoField = async (attachments = []) => {
    const uploaded = [];
    for (const attachment of attachments) {
      if (!attachment.file) {
        const payload = { ...attachment };
        delete payload.file;
        delete payload.localPreviewUrl;
        uploaded.push(payload);
        continue;
      }
      const upload = await uploadFile(null, attachment.file, {
        confirmUpload: false,
        fileType: "PHOTO",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      uploaded.push({
        id: attachment.id,
        fileId: upload?.fileId || upload?.file?.id || null,
        storageFileId: upload?.fileId || upload?.file?.id || null,
        fileName: attachment.fileName,
        description: attachment.description,
        contentType: attachment.contentType,
        size: attachment.size,
        uploaded: true,
        uploadedAt: new Date().toISOString(),
      });
    }
    return uploaded;
  };

  const handleClose = () => {
    if (saving || uploading) return;
    onClose?.();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (readOnly) return; // NEW guard
    const missingField = config.fields.find(
      (field) =>
        field.required &&
        field.type !== "photo" &&
        !String(form[field.name] || "").trim(),
    );
    if (missingField) {
      message.error(`${missingField.label} is required.`);
      return;
    }
    setSaving(true);
    try {
      const photoFields = config.fields.filter(
        (field) => field.type === "photo",
      );
      const photoUploads = {};
      for (const field of photoFields) {
        photoUploads[field.name] = await uploadPhotoField(
          form[field.name] || [],
        );
      }
      const payload = {
        ...form,
        ...photoUploads,
        workspaceId,
        readingDateTime: form.readingDateTime
          ? new Date(form.readingDateTime).toISOString()
          : undefined,
      };
      const saved = record
        ? await careService[config.apiModule].update(record.id, payload, {
            actorId,
            wid: workspaceId,
            workspaceId,
          }) // CHANGED
        : await careService[config.apiModule].create(payload, {
            actorId,
            wid: workspaceId,
            workspaceId,
          });
      message.success(`${config.label} ${record ? "updated" : "logged"}.`);
      onSaved?.(saved);
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save this record.",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderDrawerActions = (position = "bottom") => (
    <div
      className={`drawer-actions${position === "top" ? " drawer-actions-top" : ""}`}
    >
      <button
        className="mini-btn pharmacy-drawer-cancel"
        type="button"
        onClick={handleClose}
        disabled={saving || uploading}
      >
        {readOnly ? "Close" : "Cancel"}
      </button>

      {!readOnly ? (
        <button
          className="mini-btn pharmacy-primary-button"
          type="submit"
          disabled={saving || uploading}
        >
          {saving || uploading ? "Saving..." : record ? "Save Changes" : "Save"}
        </button>
      ) : null}
    </div>
  );

  const previewingUrl = attachmentUrl(previewingAttachment || {});

  return (
    <>
      <Drawer
        className="assessment-drawer medication-add-drawer compliance-evidence-drawer emergency-evacuation-drill-drawer"
        destroyOnHidden
        onClose={handleClose}
        open={open}
        title={
          readOnly
            ? `${config.label} Details`
            : record
              ? `Edit ${config.label}`
              : config.label
        }
        size="min(760px, 96vw)"
      >
        <form className="assessment-drawer-form" onSubmit={submit}>
          {renderDrawerActions("top")}
          <fieldset
            disabled={readOnly}
            style={{ border: "none", padding: 0, margin: 0, display: "flex", flexDirection:"column", gap:"20px" }}
          >
            <section className="assessment-drawer-section">
              <h3>Evidence Details</h3>
              <div className="assessment-drawer-grid medication-add-grid">
                {config.fields
                  .filter(
                    (field) =>
                      field.type !== "photo" && field.type !== "textarea",
                  )
                  .map((field) => (
                    <label className="ncp-form-field" key={field.name}>
                      <span>
                        {field.label}
                        {field.required ? " *" : ""}
                      </span>
                      {field.type === "select" ? (
                        <select
                          value={form[field.name]}
                          onChange={(event) =>
                            updateField(field.name, event.target.value)
                          }
                        >
                          {field.options.map(([value, label]) => (
                            <option value={value} key={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={
                            field.type === "datetime"
                              ? "datetime-local"
                              : field.type
                          }
                          step={field.step}
                          value={form[field.name]}
                          onChange={(event) =>
                            updateField(field.name, event.target.value)
                          }
                          required={field.required}
                        />
                      )}
                    </label>
                  ))}
              </div>
            </section>

            {config.fields.some((field) => field.type === "textarea") ? (
              <section className="assessment-drawer-section emergency-evacuation-plan-section">
                <h3>Findings and Notes</h3>
                {config.fields
                  .filter((field) => field.type === "textarea")
                  .map((field) => (
                    <label className="ncp-form-field" key={field.name}>
                      <span>
                        {field.label}
                        {field.required ? " *" : ""}
                      </span>
                      <textarea
                        rows={field.name === "notes" ? 2 : 3}
                        value={form[field.name]}
                        onChange={(event) =>
                          updateField(field.name, event.target.value)
                        }
                        required={field.required}
                      />
                    </label>
                  ))}
              </section>
            ) : null}

            {config.fields
              .filter((field) => field.type === "photo")
              .map((field) => (
                <section className="assessment-drawer-section" key={field.name}>
                  <div className="emergency-evacuation-report-attachments">
                    <div className="emergency-evacuation-report-attachments__header">
                      <div>
                        <h4>{field.label}</h4>
                        <p>Optional photo evidence to support this record.</p>
                      </div>
                      <label className="mini-btn emergency-evacuation-add-participants">
                        <i className="ti ti-camera-plus" /> Add photo
                        <input
                          accept="image/*"
                          multiple
                          onChange={(event) => addPhotos(field.name, event)}
                          type="file"
                        />
                      </label>
                    </div>
                    {(form[field.name] || []).length ? (
                      <div className="emergency-evacuation-report-attachment-list">
                        {form[field.name].map((attachment, index) => {
                          const previewUrl = attachmentUrl(attachment);
                          return (
                            <div
                              className="emergency-evacuation-report-attachment"
                              key={attachment.id}
                            >
                              <div className="emergency-evacuation-report-attachment__preview">
                                {isPhoto(attachment) && previewUrl ? (
                                  <img src={previewUrl} alt="" />
                                ) : (
                                  <i className="ti ti-file-description" />
                                )}
                              </div>
                              <div>
                                <strong>
                                  {attachmentName(attachment, index)}
                                </strong>
                                <small>
                                  Photo{" "}
                                  {formatFileSize(attachment.size)
                                    ? `| ${formatFileSize(attachment.size)}`
                                    : ""}
                                </small>
                                <label className="ncp-form-field">
                                  <span>Description</span>
                                  <input
                                    value={attachment.description || ""}
                                    onChange={(event) =>
                                      updateAttachmentDescription(
                                        field.name,
                                        attachment.id,
                                        event.target.value,
                                      )
                                    }
                                    placeholder="Describe what this photo shows"
                                  />
                                </label>
                              </div>
                              <div className="emergency-evacuation-report-attachment__actions">
                                <Tooltip title="Preview">
                                  <button
                                    className="resident-icon-button"
                                    type="button"
                                    aria-label="Preview attachment"
                                    onClick={() =>
                                      previewAttachment(attachment)
                                    }
                                  >
                                    <i className="ti ti-eye" />
                                  </button>
                                </Tooltip>
                                <button
                                  className="resident-icon-button danger"
                                  type="button"
                                  aria-label="Remove attachment"
                                  onClick={() =>
                                    removeAttachment(field.name, attachment.id)
                                  }
                                >
                                  <i className="ti ti-trash" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="emergency-evacuation-report-attachments__empty">
                        No photos added.
                      </p>
                    )}
                  </div>
                </section>
              ))}
          </fieldset>
          {renderDrawerActions("bottom")}
        </form>
      </Drawer>

      <Modal
        className="emergency-evacuation-attachment-preview-modal"
        destroyOnHidden
        footer={null}
        onCancel={() => setPreviewingAttachment(null)}
        open={Boolean(previewingAttachment)}
        title={
          previewingAttachment
            ? attachmentName(previewingAttachment)
            : "Attachment Preview"
        }
        width="min(1120px, 96vw)"
      >
        {previewingAttachment && previewingUrl ? (
          <img
            className="emergency-evacuation-attachment-preview-image"
            src={previewingUrl}
            alt={attachmentName(previewingAttachment)}
          />
        ) : (
          <p className="emergency-evacuation-report-attachments__empty">
            Preview is not available for this attachment.
          </p>
        )}
      </Modal>
    </>
  );
}

const formFromRecord = (config, record) => {
  if (!config) return {};
  if (!record) return defaultFormFor(config);
  return Object.fromEntries(
    config.fields.map((field) => {
      if (field.type === "photo") return [field.name, record[field.name] || []];
      if (field.type === "datetime" && record[field.name]) {
        const date = new Date(record[field.name]);
        const offset = date.getTimezoneOffset();
        return [
          field.name,
          new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16),
        ];
      }
      return [field.name, record[field.name] ?? ""];
    }),
  );
};
